import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { COMPARISON_ROWS } from "@/data/cardMachineProducts";

function BoolCell({ value }) {
  return value ? (
    <Check size={16} className="text-brand-aqua mx-auto" aria-label="Yes" />
  ) : (
    <Minus size={16} className="text-brand-light-grey mx-auto" aria-label="No" />
  );
}

/**
 * Reusable comparison table for the card-machine cluster. Scrolls
 * horizontally inside its own container on mobile (no page-level overflow),
 * sticky first column. On product pages pass `currentSlug` to highlight and
 * unlink the current row; on the hub omit it so every row links out.
 */
export default function ProductComparisonMatrix({
  currentSlug,
  heading = "How does it compare?",
  subheading = "Every business is different — here's the honest side-by-side across our card machine range.",
  testId,
}) {
  return (
    <section className="py-16 md:py-20 bg-white" data-testid={testId || "product-comparison"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-3 text-center">
          {heading}
        </h2>
        <p className="text-sm text-brand-grey text-center mb-8 max-w-2xl mx-auto">
          {subheading}
        </p>
        <div className="overflow-x-auto rounded-2xl border border-brand-light-grey" role="region" aria-label="Card machine comparison table" tabIndex={0}>
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="sticky left-0 bg-gray-50 px-4 py-3 font-heading font-bold text-brand-black">Product</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Best for</th>
                <th className="px-4 py-3 font-semibold text-brand-black text-center">Portable</th>
                <th className="px-4 py-3 font-semibold text-brand-black text-center">Countertop</th>
                <th className="px-4 py-3 font-semibold text-brand-black text-center">Android</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Connectivity</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Smart features</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Recommended for</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => {
                const isCurrent = row.slug === currentSlug;
                return (
                  <tr
                    key={row.slug}
                    className={`border-t border-brand-light-grey ${isCurrent ? "bg-brand-aqua-light/60" : "bg-white"}`}
                    data-testid={`comparison-row-${row.slug}`}
                  >
                    <td className={`sticky left-0 px-4 py-3 font-semibold ${isCurrent ? "bg-brand-aqua-light text-brand-black" : "bg-white"}`}>
                      {isCurrent ? (
                        <span>{`${row.product} `}<span className="text-xs font-normal text-brand-aqua">(this page)</span></span>
                      ) : (
                        <Link to={`/card-machines/${row.slug}`} className="text-brand-aqua hover:text-brand-aqua-dark transition-colors">
                          {row.product}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-grey">{row.bestFor}</td>
                    <td className="px-4 py-3 text-center"><BoolCell value={row.portable} /></td>
                    <td className="px-4 py-3 text-center"><BoolCell value={row.countertop} /></td>
                    <td className="px-4 py-3 text-center"><BoolCell value={row.android} /></td>
                    <td className="px-4 py-3 text-brand-grey whitespace-nowrap">{row.connectivity}</td>
                    <td className="px-4 py-3 text-brand-grey">{row.smartFeatures}</td>
                    <td className="px-4 py-3 text-brand-grey">{row.recommendedFor}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-brand-grey mt-3 sm:hidden text-center">Swipe to see the full comparison →</p>
      </div>
    </section>
  );
}
